import { Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { FinancialMonthlyPayment, FinancialTransactionType } from '@/model';
import { formatCurrency } from '@/utils';
import { useTranslation } from 'react-i18next';

export interface FinancialTableProps {
  data: FinancialMonthlyPayment[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onViewClick: (item: FinancialMonthlyPayment) => void;
}

export const FinancialTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
  onViewClick,
}: FinancialTableProps) => {
  const { t } = useTranslation();

  const textByFinancialType = {
    [FinancialTransactionType.MONTHLY_PAYMENT]: t('pages.financial.components.financial_table.financial_table_monthly_payment'),
    [FinancialTransactionType.EXPERIENCE]: t('pages.financial.components.financial_table.financial_table_experience'),
  };

  const columnHelper = createColumnHelper<{
    id: string;
    created_at: string;
    subscriber_name: string;
    subscription_plan: string;
    transaction_type: FinancialTransactionType;
    price: number;
  }>();

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.financial.components.financial_table.financial_table_created_at'),
    }),
    columnHelper.accessor('subscriber_name', {
      cell: props => props.getValue(),
      header: t('pages.financial.components.financial_table.financial_table_subscriber_name'),
    }),
    columnHelper.accessor('subscription_plan', {
      cell: props => props.getValue(),
      header: t('pages.financial.components.financial_table.financial_table_subscription_plan'),
    }),
    columnHelper.accessor('transaction_type', {
      cell: props => textByFinancialType[props.getValue()],
      header: t('pages.financial.components.financial_table.financial_table_transaction_type'),
    }),
    columnHelper.accessor('price', {
      cell: props => formatCurrency(props.getValue()),
      header: t('pages.financial.components.financial_table.financial_table_price'),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('pages.financial.components.financial_table.financial_table_actions'),
      cell: props => (
        <Flex justifyContent={'end'}>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.financial.components.financial_table.financial_table_show_more_actions')}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => onViewClick?.(props.row.original as FinancialMonthlyPayment)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='eye' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.financial.components.financial_table.financial_table_view_details')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={columns}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};